import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Sparkles, Loader2 } from "lucide-react";

const TYPES = ["Career","Marriage","Love","Business","Finance","Health","Education","Property","Foreign Settlement","Legal Issues","Vastu","Numerology","Gemstone","General Horoscope"];
const MODES = ["online","offline","phone","video"];
const STATUSES = ["scheduled","in-progress","completed","cancelled","no-show"];

export default function Consultations() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", date: new Date().toISOString().slice(0,10), time: "", duration_minutes: 30, mode: "offline", consultation_type: "General Horoscope", problem_description: "", fee: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [aiRunning, setAiRunning] = useState(null);

  const load = useCallback(async () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    try {
      const { data } = await api.get("/consultations", { params });
      setItems(data);
    } catch (err) { console.error("consultations load failed:", err?.message); }
  }, [statusFilter]);
  useEffect(() => {
    load();
    api.get("/clients").then(r => setClients(r.data)).catch(err => console.error("clients load failed:", err?.message));
  }, [load]);

  const create = async () => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    try {
      await api.post("/consultations", form);
      toast.success("Consultation created");
      setOpen(false);
      load();
    } catch { toast.error("Create failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/consultations/${id}`, { status });
    load();
  };

  const summarize = async (id) => {
    setAiRunning(id);
    try {
      const { data } = await api.post("/ai/summarize-consultation", { consultation_id: id });
      toast.success("AI summary generated");
      setItems(prev => prev.map(x => x.id === id ? { ...x, ai_summary: data.summary } : x));
    } catch { toast.error("AI unavailable"); }
    setAiRunning(null);
  };

  return (
    <div className="space-y-6" data-testid="consultations-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Sessions</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900 dark:text-white mt-1">Consultations</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-consultation-btn"><Plus className="w-4 h-4 mr-1.5" /> Book</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Book consultation</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Client</Label>
                <Select value={form.client_id} onValueChange={v=>setForm({...form,client_id:v})}>
                  <SelectTrigger data-testid="select-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.client_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Date"><Input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></Field>
              <Field label="Time"><Input type="time" value={form.time} onChange={e=>setForm({...form,time:e.target.value})} /></Field>
              <Field label="Duration (min)"><Input type="number" value={form.duration_minutes} onChange={e=>setForm({...form,duration_minutes:parseInt(e.target.value||0)})} /></Field>
              <Field label="Fee (₹)"><Input type="number" value={form.fee} onChange={e=>setForm({...form,fee:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Type">
                <Select value={form.consultation_type} onValueChange={v=>setForm({...form,consultation_type:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Mode">
                <Select value={form.mode} onValueChange={v=>setForm({...form,mode:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Problem description</Label>
                <Textarea rows={3} value={form.problem_description} onChange={e=>setForm({...form,problem_description:e.target.value})} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill" data-testid="submit-consultation">Book</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <Card className="p-12 rounded-2xl text-center border-dashed text-slate-500">No consultations yet.</Card>}
        {items.map(k => (
          <Card key={k.id} className="p-5 rounded-2xl border-slate-200 dark:border-slate-800 card-lift" data-testid={`consultation-card-${k.id}`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{k.consultation_type}</Badge>
                  <Badge variant="secondary">{k.mode}</Badge>
                  <Badge variant={k.status === "completed" ? "default" : "outline"}>{k.status}</Badge>
                </div>
                <div className="mt-2 font-display text-lg">{k.client_name || "Client"}</div>
                <div className="text-sm text-slate-500 font-mono">{k.date} {k.time && `• ${k.time}`} • {k.duration_minutes} min • ₹{k.fee}</div>
                {k.problem_description && <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">{k.problem_description}</div>}
                {k.ai_summary && (
                  <div className="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-xs whitespace-pre-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1">AI Summary</div>
                    {k.ai_summary}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Select value={k.status} onValueChange={v => updateStatus(k.id, v)}>
                  <SelectTrigger className="w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => summarize(k.id)} disabled={aiRunning === k.id} data-testid={`ai-summarize-${k.id}`}>
                  {aiRunning === k.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />} AI Summarize
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
